/*
 * ototo-reels.js — the reel tiles play video.
 *
 * One controller for all three UGC surfaces: the homepage "Seen in the wild"
 * rail (ds-ugc), the community wall (ds-wall) and the cart drawer strip
 * (snippets/ototo-cart-reels.liquid). Every tile is a cover image with a
 * <video> layered over it; this decides when that video loads and when it plays.
 *
 * ── HOVER ON A MOUSE, IN-VIEW ON A TOUCHSCREEN ──────────────────────────────
 * The brief was "play on hover". A phone has no hover, and 63% of this store's
 * sessions are mobile (analytics/BASELINE.md) — hover alone would mean most
 * visitors never saw a single clip move. So: pointer devices play on hover,
 * touch devices play what is on screen. Same markup, same file, one check.
 *
 * ── NOTHING LOADS UNTIL IT IS WANTED ────────────────────────────────────────
 * The address lives in `data-src` and is only assigned on the first hover or
 * the first intersection. Eight autoplaying reels on the homepage with a real
 * src would pull tens of megabytes before the shopper touched anything — the
 * same reason fs-pdp-ugc.js on fullstar carries preload="none". The cover image
 * fills the tile until then, so nothing is ever blank.
 *
 * ── THE COVER IS THE FALLBACK, NOT A PLACEHOLDER ────────────────────────────
 * The <video> sits at opacity 0 over the cover and only fades in once it is
 * really playing — `is-playing` comes from the `playing` event, never set
 * optimistically. A reel whose mp4 is missing, still transcoding on Shopify's
 * side, or refused autoplay stays a photograph rather than going black. There
 * is no state in which a tile is empty: the cover is the floor, video is the
 * upgrade.
 *
 * ⚠️ EVERYTHING IS DELEGATED, AND NEW TILES ARE PICKED UP AUTOMATICALLY. The
 * cart drawer is a section that Shopify re-renders whenever the cart changes,
 * so any handler bound once at load is gone the first time somebody adds
 * something. Hover is a delegated listener on the document, and the
 * touchscreen path watches for inserted tiles with a MutationObserver.
 *
 * ⚠️ `muted` is set as a PROPERTY as well as an attribute — Safari refuses
 * play() on a video it considers unmuted, and the attribute alone is not always
 * enough once script has touched the element.
 *
 * ⚠️ playsinline is what stops iOS taking the clip full screen on play.
 */
(function () {
  'use strict';

  /* Cheap insurance: the listeners below are on the document, so running twice
     would double every play/pause. */
  if (window.__otoReelsInit) return;
  window.__otoReelsInit = true;

  var VIDEO = '[data-ot-reel-video]';
  var TILE = '[data-ot-reel]';

  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return; /* The covers stay. Motion was the only thing declined. */
  }

  var canHover = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* Some surfaces ship two cuts. The gift doors do: a landscape one for a
     desktop and a portrait one for a phone, which is the difference between a
     letterboxed strip and a full tile. Chosen once, at load, from the width —
     not re-picked on resize, because swapping a playing video's src restarts
     the download for no visible gain. */
  var narrow = window.matchMedia && window.matchMedia('(max-width: 899px)').matches;

  function load(v) {
    if (v.dataset.loaded) return;
    var src = (narrow && v.getAttribute('data-src-mobile')) || v.getAttribute('data-src');
    if (!src) return;
    v.src = src;
    v.dataset.loaded = '1';
    v.addEventListener('playing', function () { v.classList.add('is-playing'); });
    /* A 404, a file still transcoding, or a codec the browser will not take —
       all of them must leave the cover showing rather than a black rectangle. */
    v.addEventListener('error', function () { v.classList.remove('is-playing'); });
  }

  function play(v) {
    load(v);
    if (!v.dataset.loaded) return;
    v.muted = true;
    var p = v.play();
    if (p && p.catch) p.catch(function () { /* refused, or not there: cover stays */ });
  }

  function stop(v, rewind) {
    if (!v.paused) v.pause();
    /* Back to the first frame, so the next hover replays from the top instead
       of resuming halfway through. */
    if (rewind && v.dataset.loaded) { try { v.currentTime = 0; } catch (e) {} }
    v.classList.remove('is-playing');
  }

  function videoIn(tile) { return tile ? tile.querySelector(VIDEO) : null; }

  if (canHover) {
    /* Delegated, so a drawer Shopify re-rendered a second ago behaves the same
       as a tile that was in the first paint. `mouseover`/`mouseout` bubble;
       `mouseenter`/`mouseleave` do not, which is why they are not used here. */
    document.addEventListener('mouseover', function (e) {
      var tile = e.target.closest && e.target.closest(TILE);
      if (!tile) return;
      if (tile.contains(e.relatedTarget)) return; /* moving within the same tile */
      var v = videoIn(tile);
      if (v) play(v);
    });
    document.addEventListener('mouseout', function (e) {
      var tile = e.target.closest && e.target.closest(TILE);
      if (!tile) return;
      if (tile.contains(e.relatedTarget)) return; /* still inside it */
      var v = videoIn(tile);
      if (v) stop(v, true);
    });
    /* Keyboard reaches the same behaviour — the cart tiles are links. */
    document.addEventListener('focusin', function (e) {
      var v = videoIn(e.target.closest && e.target.closest(TILE));
      if (v) play(v);
    });
    document.addEventListener('focusout', function (e) {
      var v = videoIn(e.target.closest && e.target.closest(TILE));
      if (v) stop(v, true);
    });
  } else if ('IntersectionObserver' in window) {
    /* A high threshold keeps this to the one or two tiles actually on screen
       rather than every tile in a twelve-slot wall. */
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) play(e.target);
        else stop(e.target, false);
      });
    }, { threshold: 0.6 });

    var observe = function (root) {
      var list = root.querySelectorAll ? root.querySelectorAll(VIDEO) : [];
      Array.prototype.forEach.call(list, function (v) {
        if (v.dataset.observed) return;
        v.dataset.observed = '1';
        io.observe(v);
      });
    };
    observe(document);
    new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        Array.prototype.forEach.call(m.addedNodes, function (n) {
          if (n.nodeType === 1) observe(n);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  /* Switching tabs should not leave clips running behind it. */
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) return;
    Array.prototype.forEach.call(document.querySelectorAll(VIDEO), function (v) { stop(v, false); });
  });
})();
